import {
  Alert,
  Box,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Snackbar,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@mui/joy';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import { useEffect, useReducer } from 'react';
import useBranches from '../../../../hooks/usebranches';
import ConfirmDialog from '../../../../shared/components/ConfirmDialog';
import {
  assignBranchToCohort,
  createCohort,
  deleteCohort,
  listCohortMembers,
  listCohorts,
  getCohortWarningDraft,
  sendCohortWarningEmails,
  unassignUserFromCohort,
  updateCohortSwitchOnDate,
} from '../api/mfaAdmin.api';
import type {
  CohortMemberSummary,
  CohortSummary,
} from '../types/mfaAdmin.types';

// ── State + reducer ─────────────────────────────────────────────────────────
//
// A single reducer replaces 11 individual useState hooks. Each action is a
// discrete transition — no in-place setState-plus-derive during render — so
// the state shape and the transitions are both auditable in one place.

type ConfirmKind = 'delete';

type Feedback = { message: string; color: 'success' | 'danger' };

type State = {
  cohorts: CohortSummary[];
  loading: boolean;
  error: string | null;
  form: { name: string; date: string; creating: boolean };
  editing: { cohortName: string | null; date: string; busy: boolean };
  membersFor: string | null;
  members: CohortMemberSummary[];
  confirm: { kind: ConfirmKind; cohortName: string; busy: boolean } | null;
  memberBusyId: string | null;
  // Gemeente-wide assignment, driven from the members dialog.
  assignBranch: { value: string; busy: boolean };
  // Admin-composed warning email. null = dialog closed.
  warning: {
    cohortName: string;
    subject: string;
    message: string;
    recipientCount: number;
    loading: boolean;
    busy: boolean;
  } | null;
  feedback: Feedback | null;
};

const initialState: State = {
  cohorts: [],
  loading: true,
  error: null,
  form: { name: '', date: '', creating: false },
  editing: { cohortName: null, date: '', busy: false },
  membersFor: null,
  members: [],
  confirm: null,
  memberBusyId: null,
  assignBranch: { value: '', busy: false },
  warning: null,
  feedback: null,
};

type Action =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_OK'; cohorts: CohortSummary[] }
  | { type: 'ERROR'; message: string }
  | { type: 'FORM_SET'; field: 'name' | 'date'; value: string }
  | { type: 'FORM_CREATING'; creating: boolean }
  | { type: 'FORM_RESET' }
  | { type: 'EDIT_START'; cohortName: string; date: string }
  | { type: 'EDIT_DATE'; date: string }
  | { type: 'EDIT_BUSY'; busy: boolean }
  | { type: 'EDIT_END' }
  | { type: 'MEMBERS_OPEN'; cohortName: string }
  | { type: 'MEMBERS_LOAD'; rows: CohortMemberSummary[] }
  | { type: 'MEMBERS_CLOSE' }
  | { type: 'CONFIRM_OPEN'; kind: ConfirmKind; cohortName: string }
  | { type: 'CONFIRM_BUSY'; busy: boolean }
  | { type: 'CONFIRM_CLOSE' }
  | { type: 'MEMBER_BUSY'; id: string | null }
  | { type: 'ASSIGN_BRANCH_SET'; value: string }
  | { type: 'ASSIGN_BRANCH_BUSY'; busy: boolean }
  | { type: 'WARNING_OPEN'; cohortName: string }
  | { type: 'WARNING_LOADED'; subject: string; message: string; recipientCount: number }
  | { type: 'WARNING_SET'; field: 'subject' | 'message'; value: string }
  | { type: 'WARNING_BUSY'; busy: boolean }
  | { type: 'WARNING_CLOSE' }
  | { type: 'FEEDBACK'; feedback: Feedback }
  | { type: 'FEEDBACK_CLEAR' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_OK':
      return { ...state, loading: false, cohorts: action.cohorts };
    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: action.message,
        form: { ...state.form, creating: false },
        editing: { ...state.editing, busy: false },
        confirm: state.confirm ? { ...state.confirm, busy: false } : null,
        memberBusyId: null,
        assignBranch: { ...state.assignBranch, busy: false },
        warning: state.warning ? { ...state.warning, busy: false, loading: false } : null,
      };
    case 'FORM_SET':
      return {
        ...state,
        form: { ...state.form, [action.field]: action.value },
      };
    case 'FORM_CREATING':
      return { ...state, form: { ...state.form, creating: action.creating } };
    case 'FORM_RESET':
      return {
        ...state,
        form: { name: '', date: '', creating: false },
      };
    case 'EDIT_START':
      return {
        ...state,
        editing: {
          cohortName: action.cohortName,
          date: action.date,
          busy: false,
        },
      };
    case 'EDIT_DATE':
      return { ...state, editing: { ...state.editing, date: action.date } };
    case 'EDIT_BUSY':
      return { ...state, editing: { ...state.editing, busy: action.busy } };
    case 'EDIT_END':
      return {
        ...state,
        editing: { cohortName: null, date: '', busy: false },
      };
    case 'MEMBERS_OPEN':
      return {
        ...state,
        membersFor: action.cohortName,
        members: [],
        assignBranch: { value: '', busy: false },
      };
    case 'MEMBERS_LOAD':
      return { ...state, members: action.rows };
    case 'MEMBERS_CLOSE':
      return {
        ...state,
        membersFor: null,
        members: [],
        assignBranch: { value: '', busy: false },
      };
    case 'CONFIRM_OPEN':
      return {
        ...state,
        confirm: {
          kind: action.kind,
          cohortName: action.cohortName,
          busy: false,
        },
      };
    case 'CONFIRM_BUSY':
      return {
        ...state,
        confirm: state.confirm
          ? { ...state.confirm, busy: action.busy }
          : null,
      };
    case 'CONFIRM_CLOSE':
      return { ...state, confirm: null };
    case 'MEMBER_BUSY':
      return { ...state, memberBusyId: action.id };
    case 'ASSIGN_BRANCH_SET':
      return {
        ...state,
        assignBranch: { ...state.assignBranch, value: action.value },
      };
    case 'ASSIGN_BRANCH_BUSY':
      return {
        ...state,
        assignBranch: { ...state.assignBranch, busy: action.busy },
      };
    case 'WARNING_OPEN':
      return {
        ...state,
        warning: {
          cohortName: action.cohortName,
          subject: '',
          message: '',
          recipientCount: 0,
          loading: true,
          busy: false,
        },
      };
    case 'WARNING_LOADED':
      return {
        ...state,
        warning: state.warning
          ? {
              ...state.warning,
              subject: action.subject,
              message: action.message,
              recipientCount: action.recipientCount,
              loading: false,
            }
          : null,
      };
    case 'WARNING_SET':
      return {
        ...state,
        warning: state.warning
          ? { ...state.warning, [action.field]: action.value }
          : null,
      };
    case 'WARNING_BUSY':
      return {
        ...state,
        warning: state.warning ? { ...state.warning, busy: action.busy } : null,
      };
    case 'WARNING_CLOSE':
      return { ...state, warning: null };
    case 'FEEDBACK':
      return { ...state, feedback: action.feedback };
    case 'FEEDBACK_CLEAR':
      return { ...state, feedback: null };
    default:
      return state;
  }
};

// ── Helpers ──────────────────────────────────────────────────────────────

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// Interpret the picker's YYYY-MM-DD as midnight UTC. Precise-to-the-day is
// what admins reason about; hour-level control isn't useful for rollout
// planning and would just complicate the UI.
const toSwitchOnIso = (dateInput: string): string =>
  new Date(`${dateInput}T00:00:00.000Z`).toISOString();

const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.message ?? fallback;

// ── Component ────────────────────────────────────────────────────────────

const CONFIRM_COPY: Record<
  ConfirmKind,
  { title: string; message: (cohort: string) => string; confirmLabel: string; color: 'danger' | 'primary' }
> = {
  delete: {
    title: 'Cohort verwijderen',
    message: (cohort) =>
      `Cohort "${cohort}" wordt verwijderd en alle toewijzingen op gebruikers worden vrijgegeven. Doorgaan?`,
    confirmLabel: 'Verwijderen',
    color: 'danger',
  },
};

const CohortsAdminPage = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { cohorts, loading, error, form, editing, membersFor, members, confirm, memberBusyId, assignBranch, warning, feedback } = state;
  const branchesQuery = useBranches();

  const refresh = async () => {
    try {
      dispatch({ type: 'LOAD_START' });
      const rows = await listCohorts();
      dispatch({ type: 'LOAD_OK', cohorts: rows });
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Kon de cohortlijst niet laden.'),
      });
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.date) return;
    dispatch({ type: 'FORM_CREATING', creating: true });
    try {
      await createCohort({
        name: form.name.trim(),
        switchOnDate: toSwitchOnIso(form.date),
      });
      dispatch({ type: 'FORM_RESET' });
      await refresh();
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Cohort aanmaken mislukt.'),
      });
    }
  };

  const handleSaveDate = async (name: string) => {
    if (!editing.date) return;
    dispatch({ type: 'EDIT_BUSY', busy: true });
    try {
      await updateCohortSwitchOnDate(name, {
        switchOnDate: toSwitchOnIso(editing.date),
      });
      dispatch({ type: 'EDIT_END' });
      await refresh();
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Datum bijwerken mislukt.'),
      });
    }
  };

  const confirmActionRunner = async () => {
    if (!confirm) return;
    const { cohortName } = confirm;
    dispatch({ type: 'CONFIRM_BUSY', busy: true });
    try {
      await deleteCohort(cohortName);
      dispatch({
        type: 'FEEDBACK',
        feedback: {
          color: 'success',
          message: `Cohort "${cohortName}" verwijderd.`,
        },
      });
      dispatch({ type: 'CONFIRM_CLOSE' });
      await refresh();
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Cohort verwijderen mislukt.'),
      });
    }
  };

  // Warning email is composed by the admin, not templated. The draft comes
  // from the server (single home for the default copy, date already filled
  // in) together with the recipient count, so the admin sees exactly what
  // will be sent and to how many people before committing.
  const openWarning = async (cohortName: string) => {
    dispatch({ type: 'WARNING_OPEN', cohortName });
    try {
      const draft = await getCohortWarningDraft(cohortName);
      dispatch({
        type: 'WARNING_LOADED',
        subject: draft.subject,
        message: draft.message,
        recipientCount: draft.recipientCount,
      });
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Kon het concept niet laden.'),
      });
    }
  };

  const handleSendWarning = async () => {
    if (!warning) return;
    dispatch({ type: 'WARNING_BUSY', busy: true });
    try {
      const result = await sendCohortWarningEmails(warning.cohortName, {
        subject: warning.subject.trim(),
        message: warning.message.trim(),
      });
      dispatch({ type: 'WARNING_CLOSE' });
      await refresh();
      dispatch({
        type: 'FEEDBACK',
        feedback: {
          color: 'success',
          message: `Waarschuwing verzonden naar ${result.attempted} leden.`,
        },
      });
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Waarschuwing versturen mislukt.'),
      });
    }
  };

  const openMembers = async (name: string) => {
    dispatch({ type: 'MEMBERS_OPEN', cohortName: name });
    try {
      const { members: rows } = await listCohortMembers(name);
      dispatch({ type: 'MEMBERS_LOAD', rows });
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Kon leden niet laden.'),
      });
    }
  };

  // Assign a whole gemeente to the open cohort. The server refuses a branch
  // that matches nobody, so a typo'd or renamed branch surfaces as an error
  // rather than a silent "0 assigned".
  const handleAssignBranch = async () => {
    if (!membersFor || !assignBranch.value) return;
    dispatch({ type: 'ASSIGN_BRANCH_BUSY', busy: true });
    try {
      const { modified } = await assignBranchToCohort(
        membersFor,
        assignBranch.value
      );
      const { members: rows } = await listCohortMembers(membersFor);
      dispatch({ type: 'MEMBERS_LOAD', rows });
      await refresh();
      dispatch({
        type: 'FEEDBACK',
        feedback: {
          color: 'success',
          message:
            modified === 0
              ? `Alle gebruikers uit "${assignBranch.value}" zaten al in dit cohort.`
              : `${modified} gebruiker(s) uit "${assignBranch.value}" toegevoegd.`,
        },
      });
      dispatch({ type: 'ASSIGN_BRANCH_SET', value: '' });
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Vestiging toevoegen mislukt.'),
      });
    } finally {
      dispatch({ type: 'ASSIGN_BRANCH_BUSY', busy: false });
    }
  };

  const handleUnassign = async (userId: string) => {
    dispatch({ type: 'MEMBER_BUSY', id: userId });
    try {
      await unassignUserFromCohort(userId);
      if (membersFor) {
        const { members: rows } = await listCohortMembers(membersFor);
        dispatch({ type: 'MEMBERS_LOAD', rows });
      }
      await refresh();
    } catch (err: any) {
      dispatch({
        type: 'ERROR',
        message: errorMessage(err, 'Lid verwijderen mislukt.'),
      });
    } finally {
      dispatch({ type: 'MEMBER_BUSY', id: null });
    }
  };

  const confirmCopy = confirm ? CONFIRM_COPY[confirm.kind] : null;

  return (
    <Box>
      <Typography level="h3" sx={{ mb: 2 }}>
        Cohorten
      </Typography>

      {error && (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Sheet variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 'sm' }}>
        <Typography level="title-md" sx={{ mb: 1 }}>
          Nieuw cohort
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Naam</FormLabel>
            <Input
              value={form.name}
              onChange={(e) =>
                dispatch({ type: 'FORM_SET', field: 'name', value: e.target.value })
              }
              placeholder="bijv. cohort-01"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Switch-on datum</FormLabel>
            <Input
              type="date"
              value={form.date}
              onChange={(e) =>
                dispatch({ type: 'FORM_SET', field: 'date', value: e.target.value })
              }
            />
          </FormControl>
          <Button
            onClick={handleCreate}
            loading={form.creating}
            disabled={!form.name.trim() || !form.date}
          >
            Aanmaken
          </Button>
        </Stack>
      </Sheet>

      <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
        <Table sx={{ minWidth: 780, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 180 }}>Naam</th>
              <th style={{ width: 200 }}>Switch-on datum</th>
              <th style={{ width: 80, whiteSpace: 'nowrap' }}>Leden</th>
              <th style={{ width: 200 }}>Waarschuwing verstuurd</th>
              <th style={{ width: 220 }}>Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5}>
                  <Typography level="body-sm">Bezig met laden…</Typography>
                </td>
              </tr>
            )}
            {!loading && cohorts.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <Typography level="body-sm">
                    Nog geen cohorten aangemaakt.
                  </Typography>
                </td>
              </tr>
            )}
            {cohorts.map((c) => {
              const isEditing = editing.cohortName === c.name;
              return (
                <tr key={c.name}>
                  <td>{c.name}</td>
                  <td>
                    {isEditing ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Input
                          type="date"
                          value={editing.date}
                          onChange={(e) =>
                            dispatch({ type: 'EDIT_DATE', date: e.target.value })
                          }
                          size="sm"
                        />
                        <Button
                          size="sm"
                          onClick={() => handleSaveDate(c.name)}
                          loading={editing.busy}
                        >
                          Opslaan
                        </Button>
                        <Button
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={() => dispatch({ type: 'EDIT_END' })}
                          disabled={editing.busy}
                        >
                          Annuleer
                        </Button>
                      </Stack>
                    ) : (
                      formatDate(c.switchOnDate)
                    )}
                  </td>
                  {/* Total, plus how many of them the switch-on will actually
                      affect — the number that matters when judging readiness.

                      Both lines always render, varying only their text. A bare
                      text node that appears and disappears is exactly what
                      page-translation turns into a removeChild crash; keeping
                      the elements stable means React only ever updates text it
                      still owns. translateCrashGuard is the safety net, this
                      is not needing it. */}
                  <td>
                    <span>{c.memberCount}</span>
                    <Typography level="body-xs" sx={{ display: 'block' }}>
                      <span>
                        {c.pendingCount > 0
                          ? `${c.pendingCount} nog niet ingesteld`
                          : ''}
                      </span>
                    </Typography>
                  </td>
                  <td>
                    <Stack spacing={0.5} alignItems="flex-start">
                      {c.warningSentAt ? (
                        <Chip color="success" size="sm" variant="soft">
                          {formatDate(c.warningSentAt)}
                        </Chip>
                      ) : (
                        <Chip color="neutral" size="sm" variant="soft">
                          Nog niet
                        </Chip>
                      )}
                      {/*
                        There is no scheduler in this app, so a due reminder is
                        surfaced here rather than sent automatically. Flagging
                        beats a cron job that can silently fail to run when the
                        consequence is volunteers locked out of their accounts.
                      */}
                      {c.reminderDue && (
                        <Chip color="warning" size="sm" variant="solid">
                          <span>
                            {c.daysUntilSwitchOn >= 0
                              ? `Herinnering nodig — nog ${c.daysUntilSwitchOn} dag(en), ${c.pendingCount} niet ingesteld`
                              : `${c.pendingCount} lid/leden nog niet ingesteld`}
                          </span>
                        </Chip>
                      )}
                    </Stack>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        title="Datum bewerken"
                        onClick={() =>
                          dispatch({
                            type: 'EDIT_START',
                            cohortName: c.name,
                            date: c.switchOnDate.slice(0, 10),
                          })
                        }
                      >
                        <EditRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        title="Leden tonen"
                        onClick={() => openMembers(c.name)}
                      >
                        <PeopleRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        title="Waarschuwing opstellen en versturen"
                        onClick={() => openWarning(c.name)}
                      >
                        <EmailRoundedIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        title="Cohort verwijderen"
                        onClick={() =>
                          dispatch({
                            type: 'CONFIRM_OPEN',
                            kind: 'delete',
                            cohortName: c.name,
                          })
                        }
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Stack>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>

      {/*
        Warning email composer. The admin edits subject + body before sending;
        the draft (default copy with the switch-on date filled in) comes from
        the server so there is one home for that text. The recipient count is
        shown up front — this can reach a whole gemeente.
      */}
      <Modal
        open={Boolean(warning)}
        onClose={() => dispatch({ type: 'WARNING_CLOSE' })}
      >
        <ModalDialog size="md" sx={{ minWidth: 620, maxWidth: 720 }}>
          <ModalClose />
          <DialogTitle>
            Waarschuwingsmail opstellen — &quot;{warning?.cohortName}&quot;
          </DialogTitle>
          <DialogContent>
            {warning?.loading ? (
              <Typography level="body-sm">Concept laden…</Typography>
            ) : (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Alert
                  color={warning && warning.recipientCount > 0 ? 'primary' : 'warning'}
                  variant="soft"
                >
                  <Typography level="body-sm">
                    {warning && warning.recipientCount > 0
                      ? `Deze mail gaat naar ${warning.recipientCount} lid/leden die nog geen tweestapsverificatie hebben ingesteld. Leden die het al aan hebben staan krijgen niets.`
                      : 'Er zijn op dit moment geen leden zonder tweestapsverificatie. Er valt niemand te waarschuwen.'}
                  </Typography>
                </Alert>

                <FormControl>
                  <FormLabel>Onderwerp</FormLabel>
                  <Input
                    value={warning?.subject ?? ''}
                    onChange={(e) =>
                      dispatch({
                        type: 'WARNING_SET',
                        field: 'subject',
                        value: e.target.value,
                      })
                    }
                    slotProps={{ input: { maxLength: 200 } }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Bericht</FormLabel>
                  <Textarea
                    minRows={12}
                    maxRows={20}
                    value={warning?.message ?? ''}
                    onChange={(e) =>
                      dispatch({
                        type: 'WARNING_SET',
                        field: 'message',
                        value: e.target.value,
                      })
                    }
                    slotProps={{ textarea: { maxLength: 5000 } }}
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5 }}>
                    Platte tekst. Lege regels worden alinea&apos;s in de mail.
                    De inschakeldatum staat al in het concept — pas de tekst
                    gerust aan.
                  </Typography>
                </FormControl>

                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button
                    variant="plain"
                    onClick={() => dispatch({ type: 'WARNING_CLOSE' })}
                  >
                    Annuleren
                  </Button>
                  <Button
                    onClick={handleSendWarning}
                    loading={warning?.busy}
                    disabled={
                      !warning ||
                      warning.recipientCount === 0 ||
                      !warning.subject.trim() ||
                      !warning.message.trim()
                    }
                  >
                    Versturen
                  </Button>
                </Stack>
              </Stack>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>

      <Modal
        open={Boolean(membersFor)}
        onClose={() => dispatch({ type: 'MEMBERS_CLOSE' })}
      >
        <ModalDialog size="md" sx={{ minWidth: 520 }}>
          <ModalClose />
          <DialogTitle>Leden van &quot;{membersFor}&quot;</DialogTitle>
          <DialogContent>
            {/*
              Gemeente-wide assignment. This is the rollout unit the org
              thinks in — adding a branch enrols everyone in it, rather than
              picking hundreds of people one at a time.
            */}
            <Sheet
              variant="soft"
              sx={{ p: 2, borderRadius: 'sm', mb: 2 }}
            >
              <Stack direction="row" gap={1} alignItems="flex-end">
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Vestiging toevoegen</FormLabel>
                  <Select
                    placeholder="Kies een vestiging"
                    value={assignBranch.value || null}
                    onChange={(_, value) =>
                      dispatch({
                        type: 'ASSIGN_BRANCH_SET',
                        value: (value as string) ?? '',
                      })
                    }
                  >
                    {(branchesQuery.data ?? []).map((branch: any) => (
                      <Option key={branch.name} value={branch.name}>
                        {branch.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  onClick={handleAssignBranch}
                  loading={assignBranch.busy}
                  disabled={!assignBranch.value}
                >
                  Toevoegen
                </Button>
              </Stack>
              <Typography level="body-xs" sx={{ mt: 1 }}>
                Iedereen in deze vestiging wordt aan het cohort toegevoegd.
                Vanaf de inschakeldatum moeten zij tweestapsverificatie
                instellen om te kunnen inloggen.
              </Typography>
            </Sheet>

            <Table size="sm" sx={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '45%' }}>Email</th>
                  <th style={{ width: '25%' }}>Naam</th>
                  <th style={{ width: 70 }}>2FA</th>
                  <th style={{ width: 110 }}>Actie</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 && (
                  <tr>
                    <td colSpan={4}>
                      <Typography level="body-sm">
                        Geen leden gevonden.
                      </Typography>
                    </td>
                  </tr>
                )}
                {members.map((m) => (
                  <tr key={m._id}>
                    <td style={{ overflowWrap: 'anywhere' }}>{m.email}</td>
                    <td style={{ overflowWrap: 'anywhere' }}>
                      {[m.firstName, m.lastName].filter(Boolean).join(' ') ||
                        '-'}
                    </td>
                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={m.mfaEnabled ? 'success' : 'neutral'}
                      >
                        {m.mfaEnabled ? 'Aan' : 'Uit'}
                      </Chip>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="soft"
                        color="danger"
                        onClick={() => handleUnassign(m._id)}
                        loading={memberBusyId === m._id}
                      >
                        Verwijderen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </DialogContent>
        </ModalDialog>
      </Modal>

      {confirm && confirmCopy && (
        <ConfirmDialog
          isOpen
          title={confirmCopy.title}
          message={confirmCopy.message(confirm.cohortName)}
          confirmLabel={confirmCopy.confirmLabel}
          confirmColor={confirmCopy.color}
          loading={confirm.busy}
          onCancel={() => dispatch({ type: 'CONFIRM_CLOSE' })}
          onConfirm={confirmActionRunner}
        />
      )}

      <Snackbar
        autoHideDuration={4000}
        open={Boolean(feedback)}
        onClose={() => dispatch({ type: 'FEEDBACK_CLEAR' })}
        color={feedback?.color ?? 'neutral'}
        variant="soft"
      >
        {feedback?.message}
      </Snackbar>
    </Box>
  );
};

export default CohortsAdminPage;
